import { FlowItem } from "../services/storage-types";
import { FlowRun } from "./FlowRun";
import { PowerBase } from "./PowerBase";

export function onlyUnique(value, index, self) {
    return self.indexOf(value) === index;
}

/** Normalise any incoming source value (string | string[] | null) into a deduped string[]. */
export function normalizeSources(input: any): string[] {
    if (!input) return [];
    const arr = Array.isArray(input) ? input : [input];
    return arr.map((s) => String(s || '').trim()).filter(Boolean).filter(onlyUnique);
}

/** Union two source lists, deduped. */
export function mergeSources(a: string[] | null | undefined, b: string[] | null | undefined): string[] {
    return normalizeSources([...(a || []), ...(b || [])]);
}

export function getRunColour(status) {
    if (/succeeded/gi.test(status)) {
        return '#228B22';
    }
    if (status == 'Failed') {
        return '#C21807';
    }
    if (status == 'Test Failed' || /cancel/gi.test(status)){
        return 'gray';
    }
    if (/running/gi.test(status)){
        return 'dodgerblue';
    }
    return 'gray';
}

export class Flow extends PowerBase implements FlowItem {
    // environment?: string;
    // _id: string;
    flowName: string;
    // displayName: string;
    flowState: string;
    modified: Date;
    _sharingType?: string;
    runs: FlowRun[] = [];
    definition?: any;
    connections?: any[];
    _type: string = "flow";
    limited?: boolean = false;
    loading?: boolean = false;
    description?: string = null;
    triggers: any[] = [];
    triggerUrl: string = '';
    deprecatedTriggerUrl: string = '';
    lastRunTriggerUrlChecked?:boolean = undefined;
    lastRunTriggerCaller?:string = undefined;
    //more: boolean = false;
    nextLink: string = '';
    //raw: any;
    userType: string = ''; // ownerType
    owners: any[] = [];
    actionsCount: number = 0;
    actionsTooltip: string = '';
    solution?: string = null;
    //actions = [];
    favourite: boolean = false;
    flowSuspensionReason: string;
    isManaged: boolean = false;
    workflowEntityId: string = null;
    workflowUniqueId: string = null;
    creatorOid: string = null;
    scanned: Date = null;
    runScanQueuedAt?: number = null;
    runScanReason?: string = null;
    runScanAttempts?: number = 0;
    runScanLastError?: string = null;
    /**
     * Origins this flow was discovered through (e.g. 'my', 'shared', 'admin', 'graphql').
     * A single flow can appear in multiple lists — admin scans see flows that the user
     * can also see as 'my' or 'shared'. The cache merges sources so a flow has one row.
     */
    sources: string[] = [];

    // public runsCount: number = 0;
    // public runsFailCount: number = 0;
    // public triggerFailureCount: number = 0;
    //public runsEstimate: number = 0;
    // lastRunTimestamp: string;

    private _sparkline = [];
    private _triggerIcon: string = '';
    private _environment = null;
    private _monitored: boolean;
    //checkRuns: number = 0; // default 0, skip -1, >0 runs

    //a number to suggest when to perform next scan
    //nextScanTick: number = 0;

    constructor(init?: any) {
        super(init);
        if (init) {
            this.initialize(init);
        }
    }

    initialize(item: any) {
        this._id = super.name = this.flowName = item.name;
        this.sources = normalizeSources(
            item._sources ?? item.properties?._sources ?? item._source ?? item.properties?._source
        );
        this.displayName = item.properties.displayName;
        this.flowState = item.properties.state;
        this.flowSuspensionReason = item.properties['flowSuspensionReason'];
        this.modified = item.properties.lastModifiedTime ? new Date(item.properties.lastModifiedTime) : null;
        this.sharingType = item.properties['sharingType'] || '';
        this.environment = item.properties.environment.name;
        if (item.properties.definitionSummary?.triggers) {
            this.resolveTriggers(item.properties.definitionSummary, item.properties.connectionReferences);
        }
        this.description = item.properties.definitionSummary?.description;

        // Solution-aware: workflowEntityId is set by Dataverse-backed flows. Admin LIST
        // doesn't return sharingType, so the presence of workflowEntityId alone is the
        // reliable signal. workflowUniqueId is the cross-environment stable component ID.
        this.workflowEntityId = item.properties.workflowEntityId || null;
        if (this.workflowEntityId) {
            this.solution = this.workflowEntityId;
        }
        this.workflowUniqueId = item.properties.workflowUniqueId || null;
        this.isManaged = !!item.properties.isManaged;
        this.creatorOid = item.properties.creator?.objectId || item.properties.creator?.userId || null;

        this.raw = null;//item;

        this.userType = item.properties['userType'];
        if (this.sharingType != "CommonDataService" && this.userType != "Owner") {
//            debugger
        }

        if (item.properties.definitionSummary?.actions) {
            this.resolveActions(item.properties.definitionSummary.actions);
        }

        if (item.properties.definition) {
            this.definition = item.properties.definition;
        }

        // {
        //     "userType": "Owner",
        //     "creator": {
        //         "tenantId": "26e65220-5561-46ef-9783-ce5f20489241",
        //         "objectId": "36e3b856-3c2e-4605-ac5f-99064d229c3a",
        //         "userId": "36e3b856-3c2e-4605-ac5f-99064d229c3a",
        //         "userType": "ActiveDirectory"
        //     }
        // }        
    }

    public get name() {
        return this.flowName;
    }

    set raw(raw: any) {
        // do nothing
        if (raw && raw.properties) {
            if (raw.properties.userType) {
                this.userType = raw.properties.userType;
            }
            if (raw.properties.definitionSummary.actions) {
                this.resolveActions(raw.properties.definitionSummary.actions);
            }
        }
    }

    set env(environment: any) {
        this._environment = environment;
    }

    get portalUrl(): string {
        return `/manage/environments/${this.environment}/flows/${this.sharingType=='Coauthor'?'shared/':''}${this.flowName}/details`;
    }
    // get runs(): FlowRun[] {
    //     return this._runs;
    // }

    // set runs(runs: FlowRun[]) {
    //     this._runs = runs;
    // }

    get runsCount(): number {
        return this.runs.length;
    }

    get runsFailCount(): number {
        return this.runs.filter((run) => run.status == 'Failed').length;
    }

    get more(): boolean {
        return /* this.runs.length > 0 && */ !!this.nextLink;
    }

    get runsEstimate(): number {
        //console.log('run-estimate...')
        if (this.runs?.length > 1) {

            var earliest = new Date(Math.min(...this.runs.map(run=>run.startTime.getTime())));
            var latest = new Date(Math.max(...this.runs.map(run=>run.startTime.getTime())));

            const totalDurationDays = Math.floor((latest.getTime() - earliest.getTime()) / (1000 * 60 * 60 * 24));
            //const totalDurationMonths = (latest.getFullYear() - earliest.getFullYear()) * 12 + (latest.getMonth() - earliest.getMonth());

            return (totalDurationDays > 0 ? this.runs.length / totalDurationDays : this.runs.length) * 30;
        }
        return null;
    }

    get runsEstimateText(): string {
        //console.log('run-estimate...')
        if (this.runs?.length > 1) {

            var earliest = new Date(Math.min(...this.runs.map(run=>run.startTime.getTime())));
            var latest = new Date(Math.max(...this.runs.map(run=>run.startTime.getTime())));
            
            const totalDurationDays = Math.floor((latest.getTime() - earliest.getTime()) / (1000 * 60 * 60 * 24));
            if (this.runs.length < 50) {
                return `${this.runs.length} in ${totalDurationDays} days`;
            }
            if (totalDurationDays > 0) {
                return `${Math.round((this.runs.length / totalDurationDays) * 30)} / month est.`; 
            }

            const totalDurationHours = Math.floor((latest.getTime() - earliest.getTime()) / (1000 * 60 * 60));

            if (totalDurationHours > 0) {
                return `${Math.round((this.runs.length / totalDurationHours))} / hour est.`; 
            }

            const totalDurationMins = Math.floor((latest.getTime() - earliest.getTime()) / (1000 * 60));
            if (totalDurationMins > 0) {
                return `${Math.round((this.runs.length / totalDurationMins))} / hour est.`; 
            }
        }
        return null;
    }

    get runsSort(): number {
        return this.runsEstimate || this.runsCount;
    }

    get lastRun(): Date {
        return (this.runs && this.runs.length > 0 && this.runs[0].startTime) ? this.runs[0].startTime : null;
    }

    get tags(): string[] {
        return (this.description || '').split(/\s+/).filter(x => x[0] == '#').map(x => x.toLowerCase());
    }

    get tagsText(): string {
        return (this.tags || []).join(', ');
    }

    get shareOwners(): string[] {
        //let shares = this.sharingType.split(',').map((t)=>t.trim());
        let owners = (this.owners || []).map((owner)=>{
            const {type, id} = owner?.properties?.principal || {};
            if (type == 'User') {
                return `${id}`;
                // const u = users.find(user => user.key.endsWith(id));
                // return u ? u.displayName : id;
            }
            return null;
        });
        return [/*...shares,*/ ...owners].filter(t=>!!t);
    }

    get trigger(): string {
        return (this.triggers || []).map(trigger => trigger.type).join(',');
    }

    get triggerIcon(): string {
        if (this._triggerIcon) {
            return this._triggerIcon;
        }
        else {
            this._triggerIcon = (this.triggers || []).map(trigger => trigger.icon).join(',');
            return this._triggerIcon;
        }
    }

    get icon() {
        return this.triggerIcon;
    }

    get type() {
        return this._type;
    }
    set type(value: string) {
        this._type = value;
    }

    get sharingType() {
        return this._sharingType;
    }
    set sharingType(value: string) {
        this._sharingType = value;
    }

    get triggerKind(): string {
        return (this.triggers || []).map(trigger => trigger.kind).join(',');
    }

    get triggerTooltip(): string {
        return this.triggerUrl;
    }

    get triggerMethod(): string {
        // triggers has manual trigger
        if ((this.triggers || []).find(t => t.type == 'Request')) {

            // definition has request trigger
            if (this.definition && this.definition.triggers) {
                var keys = Object.keys(this.definition.triggers);
                for (var i = 0; i < keys.length; i++) {
                    var trig = this.definition.triggers[keys[i]];
                    if (trig.type == 'Request') {
                        if (trig.inputs && trig.inputs.method) {
                            return trig.inputs.method.toUpperCase();
                        }   
                        if (trig.kind == 'Http' || trig.kind == 'TeamsWebhook') {
                            return 'POST (default)';
                        }
                    }
                }
            }
        }
        return null;
    }

    get flowActions(): string[] {
        return (this.actionsTooltip || '').split(',').filter(a=> /\./.test(a)).map(a => {
            return a;
        });
    }

    get runsLoaded(): FlowRun[] {
        return this.loading ? [] : this.runs;
    }

    get runsSparkline(): any[]{
        if (this._sparkline && (this._sparkline.length >= 20 || this.runs.length == this._sparkline.length)) {
            return this._sparkline;
        }
        if (this.runs && this.runs.length > 0) {
            this._sparkline = this.runs.slice(0, 20).map(run=>{
                return [
                    run.duration,
                    null,
                    getRunColour(run.status)
                    //( /succeeded/gi.test(run.status) ? '#228B22' : '#C21807')
                ]
            });
            return this._sparkline;
        }
    }

    get ownerIds(): string[] {
        if (!this.owners || this.owners.length == 0) {
            return [];
        }

        return this.owners.filter(p=>!!p.properties && !!p.properties.principal).map((p)=>{
            let { id } = p.properties['principal'];
            return id;
        })
    }

    // get owner(): string {
    //     return this.raw.properties.userType;
    // }

    // get actionsCount() {

    //     if (this.raw.properties.definitionSummary.actions) {
    //         return this.raw.properties.definitionSummary.actions.length;
    //     }
    //     return null;
    // }

    // onlyUnique(value, index, self) {
    //     return self.indexOf(value) === index;
    // }

    // get actionsTooltip() {
    //     if (this.raw.properties.definitionSummary.actions) {
    //         var actions = this.raw.properties.definitionSummary.actions.map(a=>{
    //             if (a.api && a.api.name) {
    //                 if (a.metadata && a.metadata.flowSystemMetadata && a.metadata.flowSystemMetadata.swaggerOperationId) {
    //                     return `${a.type}(${a.api.name}.${a.metadata.flowSystemMetadata.swaggerOperationId})`
    //                 }
    //                 return `${a.type}(${a.api.name})`
    //             }
    //             return a.type;
    //         });

    //         var uniques = actions.filter(this.onlyUnique);

    //         uniques = uniques.map(name=>{
    //             var count = actions.filter(action=>action==name).length;
    //             return count == 1 ? name : `${name} (${count})`;
    //         });

    //         return uniques.join(',');
    //     }
    //     return null;
    // }

    get actions(): string[] {
        var actions = [];

        // actions.push({
        //     text: 'Edit',
        //     items: [{
        //         text:"Edit",
        //         items: [
        //             {
        //                 "text": "Edit JSON"
        //             },                
        //             {
        //                 "text": "Edit in Designer"
        //             },
        //             {
        //                 "text": "Edit",
        //                 "url": `https://flow.microsoft.com/manage/environments/${this.environment}/flows/${(this.sharingType == 'Coauthor' ? 'shared/' : '')}${this.name}`
        //             }
        //         ]
        //     }]
        // })
        actions.push({
            text: 'New',
            items: [{
                text: 'New',
                items: [
                    {
                        text: 'Blank',
                        url: `https://flow.microsoft.com/manage/environments/${this.environment}/flows/new`
                    },
                    {
                        text: 'Instant – Flow Button',
                        url: `https://flow.microsoft.com/manage/environments/${this.environment}/flows/new?trigger=providers/Microsoft.ProcessSimple/operationGroups/Flow/operations/FlowButton`
                    },
                    {
                        text: 'Instant – PowerApps',
                        url: `https://flow.microsoft.com/manage/environments/${this.environment}/flows/new?trigger=providers/Microsoft.ProcessSimple/operationGroups/PowerApps/operations/PowerAppsButton`
                    },
                    {
                        text: 'Automated – SP item created or modified',
                        url: `https://flow.microsoft.com/manage/environments/${this.environment}/flows/new?trigger=/providers/Microsoft.PowerApps/apis/shared_sharepointonline/apiOperations/GetOnUpdatedItems`
                    },
                    {
                        text: 'Automated – SP file created or modified',
                        url: `https://flow.microsoft.com/manage/environments/${this.environment}/flows/new?trigger=/providers/Microsoft.PowerApps/apis/shared_sharepointonline/apiOperations/GetOnUpdatedFileItems`
                    },
                    {
                        text: 'Automated – CDS record created',
                        url: `https://flow.microsoft.com/manage/environments/${this.environment}/flows/new?trigger=/providers/Microsoft.PowerApps/apis/shared_commondataservice/apiOperations/SubscribeOnNewItems`
                    },
                    {
                        text: 'Automated – CDS record updated',
                        url: `https://flow.microsoft.com/manage/environments/${this.environment}/flows/new?trigger=/providers/Microsoft.PowerApps/apis/shared_commondataservice/apiOperations/SubscribeOnUpdatedItems`
                    },
                ]
            }]
        });

        actions.push({
            "text": "Edit",
            "url": `https://flow.microsoft.com/manage/environments/${this.environment}/flows/${(this.sharingType == 'Coauthor' ? 'shared/' : '')}${this.flowName}`
        });

        actions.push("Edit in Designer");

        actions.push("Get Latest Runs");
        if (this.runsCount >= 50 || this.more) { actions.push("Get More Runs (slow)"); }

        if (this.trigger == "Request" && this.triggerKind == "Button") {
            actions.push("Run Now");
        }

        return actions;
    }

    get adminActions(): string[] {
        var actions = [];

        actions.push({
            "text": "Edit",
            "url": `https://flow.microsoft.com/manage/environments/${this.environment}/flows/${(this.sharingType == 'Coauthor' ? 'shared/' : '')}${this.flowName}`
        });

        actions.push("Get Latest Runs");
        if (this.more) { actions.push("Get More Runs (slow)"); }

        return actions;
    }

    resolveTriggers(definitionSummary, connectionReferences) {
        this.triggers = definitionSummary.triggers.map(trigger => {

            if (['OpenApiConnectionWebhook', 'OpenApiConnection', 'ApiConnection', 'ApiConnectionWebhook'].includes(trigger.type)) {
                var trig = {
                    type: '',
                    kind: '',
                    icon: ''
                };
                if (trigger.api) {
                    if (trigger.api.properties) {
                        trig.type = trigger.api.properties.displayName;
                        trig.icon = trigger.api.properties.iconUri;
                    }
                    else if (trigger.api.name) {
                        var apiName = trigger.api.name;
                        trig.type = apiName;

                        if (connectionReferences && connectionReferences[apiName]) {
                            var reference = connectionReferences[apiName];

                            trig.type = reference.displayName;
                            trig.icon = reference.iconUri;
                        }

                    }
                }
                if (trigger.metadata && trigger.metadata.flowSystemMetadata) {
                    trig.kind = trigger.metadata.flowSystemMetadata.swaggerOperationId;
                }
                return trig;
            }
            if (trigger.type == 'Request' && ['OpenApiConnectionWebhook', 'OpenApiConnection', 'ApiConnection', 'ApiConnectionWebhook'].includes(trigger.kind)) {
                var trig = {
                    type: '',
                    kind: '',
                    icon: ''
                };
                if (trigger.api) {
                    if (trigger.api.properties) {
                        trig.type = trigger.api.properties.displayName;
                        trig.icon = trigger.api.properties.iconUri;
                    }
                    else if (trigger.api.name) {
                        var apiName = trigger.api.name;
                        trig.type = apiName;

                        if (connectionReferences && connectionReferences[apiName]) {
                            var reference = connectionReferences[apiName];

                            trig.type = reference.displayName;
                            trig.icon = reference.iconUri;
                        }

                    }
                }
                // if (trigger.metadata && trigger.metadata.flowSystemMetadata) {
                //     trig.kind = trigger.metadata.flowSystemMetadata.swaggerOperationId;
                // }
                return trig;
            }
            var result = {
                type: trigger.type,
                kind: trigger.kind,
                icon: 'data:image/svg+xml;base64,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'
            };

            switch (result.kind) {
                case 'Button':
                    result.icon = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGZpbGw9IiMwN0YiIGQ9Ik0wIDBoMzJ2MzJIMHoiLz48cGF0aCBkPSJNMTMuMjUgMTQuMjRWMTAuNWEyLjUgMi41IDAgMCAxIDUgMHYzLjc0YzEuMjEtLjgxIDItMi4xOCAyLTMuNzQgMC0yLjQ5LTIuMDEtNC41LTQuNS00LjVzLTQuNSAyLjAxLTQuNSA0LjVjMCAxLjU2Ljc5IDIuOTMgMiAzLjc0em05Ljg0IDQuNjNsLTQuNTQtMi4yNmMtLjE3LS4wNy0uMzUtLjExLS41NC0uMTFoLS43NnYtNmMwLS44My0uNjctMS41LTEuNS0xLjVzLTEuNS42Ny0xLjUgMS41djEwLjc0bC0zLjQzLS43MmMtLjA4LS4wMS0uMTUtLjAzLS4yNC0uMDMtLjMxIDAtLjU5LjEzLS43OS4zM2wtLjc5LjggNC45NCA0Ljk0Yy4yNy4yNy42NS40NCAxLjA2LjQ0aDYuNzljLjc1IDAgMS4zMy0uNTUgMS40NC0xLjI4bC43NS01LjI3Yy4wMS0uMDcuMDItLjE0LjAyLS4yIDAtLjYyLS4zOC0xLjE2LS45MS0xLjM4eiIgZmlsbD0iI0ZGRiIvPjwvZz48L3N2Zz4=';
                    break;
                case 'Http':
                    result.icon = 'data:image/svg+xml;base64,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';
                    break;
                case 'PowerApp':
                    result.icon = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA5MC4xNTUgOTAuMTU1Ij48cGF0aCBmaWxsPSIjNzYyNzc1IiBkPSJNMCAwaDkwLjE1NXY5MC4xNTVIMHoiLz48cmVjdCB4PSI1MC4yMzkiIHk9IjUwLjE1NCIgd2lkdGg9IjEzLjQ3MSIgaGVpZ2h0PSIxMy40NzEiIHJ4PSIyLjY5NCIgcnk9IjIuNjk0IiB0cmFuc2Zvcm09InJvdGF0ZSgtNDQuOTk3IDU2Ljk3NCA1Ni44ODkpIiBmaWxsPSIjZmZmIi8+PHJlY3QgeD0iNjIuMTQ1IiB5PSIzOC4yMyIgd2lkdGg9IjEzLjQ3IiBoZWlnaHQ9IjEzLjQ3IiByeD0iMi42OTQiIHJ5PSIyLjY5NCIgdHJhbnNmb3JtPSJyb3RhdGUoLTQ0Ljk5NyA2OC44ODEgNDQuOTY1KSIgZmlsbD0iI2ZmZiIvPjxwYXRoIGQ9Ik01Mi43MDkgNDMuMDZsLTEuODYtMS44Ni0xNS43NjItMTUuNzYxYTIuNyAyLjcgMCAwIDAtMy44MSAwTDE1LjUxNCA0MS4ybC0xLjg1OSAxLjg2YTIuNyAyLjcgMCAwIDAgMCAzLjgxbDEuODU5IDEuODU5TDMxLjI3NyA2NC40OWEyLjcgMi43IDAgMCAwIDMuODEgMGwxNS43NjItMTUuNzYxIDEuODU5LTEuODU5YTIuNyAyLjcgMCAwIDAgMC0zLjgxbS0yMS40MzEgOS41MjVsLTQuMjU1LTQuMjU1LTEuNDYtMS40NmEyLjcgMi43IDAgMCAxIDAtMy44MWw1LjcxNS01LjcxNWEyLjcgMi43IDAgMCAxIDMuODEgMEw0MC44IDQzLjA2YTIuNyAyLjcgMCAwIDEgMCAzLjgxbC0xLjQ2MSAxLjQ2LTQuMjU1IDQuMjU1YTIuNyAyLjcgMCAwIDEtMy44MSAwbTI3LjYwNS0xMS45MDZsNS43MTUtNS43MTVhMi43IDIuNyAwIDAgMCAwLTMuODFsLTUuNzE1LTUuNzE1YTIuNyAyLjcgMCAwIDAtMy44MSAwbC01LjcxNSA1LjcxNWEyLjcgMi43IDAgMCAwIDAgMy44MWw1LjcxNSA1LjcxNWEyLjcgMi43IDAgMCAwIDMuODEgMCIgZmlsbD0iI2ZmZiIvPjwvc3ZnPg==';
                    break;
                case 'HttpWebhook':
                    result.icon = 'data:image/svg+xml;base64,PHN2ZyBpZD0iTGF5ZXJfMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiI+PHN0eWxlPi5zdDF7ZmlsbDojZmZmfTwvc3R5bGU+PHBhdGggZmlsbD0iIzdmYmEwMCIgZD0iTTAgMGgzMnYzMkgweiIvPjxwYXRoIGNsYXNzPSJzdDEiIGQ9Ik0yMS42IDIzLjFjMS4yIDAgMi4yLTEgMi4yLTIuMiAwLTEuMi0xLTIuMi0yLjItMi4yLS45IDAtMS43LjUtMiAxLjNoLTUuN3YuOWMwIDItMS42IDMuNS0zLjUgMy41LTIgMC0zLjUtMS42LTMuNS0zLjUgMC0xLjcgMS4xLTMuMSAyLjctMy40bC0uNC0xLjhjLTIuNS41LTQuMiAyLjctNC4yIDUuMiAwIDMgMi40IDUuNCA1LjQgNS40IDIuNiAwIDQuOC0xLjkgNS4zLTQuNGg0Yy4zLjcgMSAxLjIgMS45IDEuMnoiLz48cGF0aCBjbGFzcz0ic3QxIiBkPSJNOC4xIDIwLjljMCAxLjIgMSAyLjIgMi4yIDIuMnMyLjItMSAyLjItMi4yYzAtLjUtLjItLjktLjQtMS4zbDIuOS01LjEtMS42LS45Yy0uNy0uNy0xLjEtMS42LTEuMS0yLjUgMC0yIDEuNi0zLjUgMy41LTMuNSAyIDAgMy41IDEuNiAzLjUgMy41IDAgLjMgMCAuNi0uMS45bDEuOC41Yy4xLS40LjItLjkuMi0xLjQgMC0zLTIuNC01LjQtNS40LTUuNHMtNS40IDIuNC01LjQgNS40YzAgMS42LjcgMy4xIDEuOSA0LjFsLTIgMy41aC0uMmMtMSAwLTIgMS0yIDIuMnoiLz48cGF0aCBjbGFzcz0ic3QxIiBkPSJNMjEuNiAxNS41Yy0uNiAwLTEuMy4xLTEuOS4zbC0yLTMuNWMuMy0uNC40LS44LjQtMS4zIDAtMS4yLTEtMi4yLTIuMi0yLjJzLTIuMiAxLTIuMiAyLjIgMSAyLjIgMi4yIDIuMmguMmwyLjkgNS4xIDEuNi0uOWMuMy0uMS42LS4xLjktLjEgMiAwIDMuNSAxLjYgMy41IDMuNSAwIDItMS42IDMuNS0zLjUgMy41LS45IDAtMS44LS40LTIuNS0xbC0xLjMgMS4zYzEgMSAyLjQgMS42IDMuOCAxLjYgMyAwIDUuNC0yLjQgNS40LTUuNHMtMi4zLTUuMy01LjMtNS4zeiIvPjwvc3ZnPg==';
                    break;
                case 'Geofence':
                    result.icon = 'data:image/svg+xml;base64,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';
                    break;
            }
            if (result.type == 'Recurrence') {
                result.icon = 'data:image/svg+xml;base64,PHN2ZyBpZD0iTGF5ZXJfMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiI+PHN0eWxlPi5zdDF7ZmlsbDojZmZmfTwvc3R5bGU+PHBhdGggZmlsbD0iIzFmODVmZiIgZD0iTTAgMGgzMnYzMkgweiIvPjxwYXRoIGNsYXNzPSJzdDEiIGQ9Ik0xNi4wMiA3Ljk0Yy01LjI4LS4wMS05LjU2IDQuMjctOS41NSA5LjUzLjAxIDUuMjcgNC4yNyA5LjUzIDkuNTIgOS41M3M5LjUzLTQuMjcgOS41NC05LjUyYy4wMS01LjI0LTQuMjctOS41NC05LjUxLTkuNTR6bS0uMDQgMTYuOTVhNy4zOTMgNy4zOTMgMCAwIDEtNy40LTcuNDVjLjAyLTQuMjYgMy41Ny03LjUxIDcuNzEtNy4zOSAzLjg3LjEyIDcuMTYgMy40OCA3LjEyIDcuNDItLjAxIDQuMTEtMy4yOSA3LjQ0LTcuNDMgNy40MnoiLz48cGF0aCBjbGFzcz0ic3QxIiBkPSJNMjQuMTggMTAuNjljLjE5LS40OC4zNi0uOTUuNTctMS40LjMzLS42OS4xNS0xLjI5LS4zNi0xLjc3YTEwLjEzMSAxMC4xMzEgMCAwIDAtNC4yNC0yLjQzYy0uNS0uMTQtMS4wNS0uMTYtMS40My4yNy0uNDMuNDgtLjc5IDEuMDMtMS4yIDEuNTggMi42Ny40OSA0Ljg1IDEuNzYgNi42NiAzLjc1em0tMTYuMzktLjAzYzEuOC0yLjAxIDMuOTgtMy4yNSA2LjYyLTMuNzUtLjMyLS40NS0uNjMtLjg1LS45LTEuMjYtLjM3LS41OC0uOS0uNzMtMS41My0uNjEtMS40My4yOC0yLjYzIDEuMDQtMy43OSAxLjg4LS4yNS4xOC0uNDguNDEtLjY5LjY0LS4zNi4zOS0uNTYuODUtLjQgMS4zNy4xOS41Ny40NSAxLjEzLjY5IDEuNzN6bTguOTIgMS4xMWMtLjgzLS40NS0xLjc0LjA3LTEuNzQgMS4wMS4wMSAxLjMzLjAyIDIuNjYuMDUgNCAuMDEuMjYtLjA2LjQ1LS4yNy42My0uNTMuNDQtMS4wNi44OS0xLjU2IDEuMzYtLjQzLjQtLjQyIDEuMDEtLjAyIDEuNDUuMzkuNDMgMS4wMi41IDEuNDUuMTMuNzQtLjY0IDEuNDctMS4yOCAyLjE4LTEuOTUuMTUtLjE0LjI5LS40LjI5LS42LjAzLS45My4wMS0xLjg3LjAxLTIuOGguMDV2LTIuMzhjLjAxLS4zNy0uMS0uNjYtLjQ0LS44NXoiLz48L3N2Zz4=';
            }

            return result;
        });
    }

    resolveActions(definitionActions: any[]) {
        this.actionsCount = definitionActions.length;

        var actions = definitionActions.map(a => {
            let type = a.type;
            if (a.api && a.api.name) {
                if (a.type == 'OpenApiConnection' || a.type == 'OpenApiConnectionWebhook') {
                    // connection reference
                    type = `${a.api.name}`
                }
                if ((a.type == 'ApiConnection' || a.type == 'ApiConnectionWebhook')) {
                    // connection reference
                    type = `${a.api.name}`
                }
                if (a.swaggerOperationId) {
                    return `${type}.${a.swaggerOperationId}`;
                }
                if (a.metadata && a.metadata.flowSystemMetadata && a.metadata.flowSystemMetadata.swaggerOperationId) {
                    return `${type}.${a.metadata.flowSystemMetadata.swaggerOperationId}`
                }

                return `${a.api.name}`
            }
            return type;
        });

        var uniques = actions.filter(onlyUnique).map(name => {
            var count = actions.filter(action => action == name).length;
            return count == 1 ? name : `${name} (${count})`;
        });

        this.actionsTooltip = uniques.join(',');

        //this._actions.push(...actions.filter(onlyUnique));

    }

    setNextScan(length: number = 0) {
        // need some way to determine not to scan so frequently, but not sure what that is
        // length = length || this.runs.length;

        // if (length == 0) {
        //     this.nextScanTick = Date.now() + 1000 * 60 * 60 * 24 * 3;   // 3 day offset?
        //     //flow.checkRuns = flow.checkRuns -1; // skip next time
        // }
        // else {
        //     this.nextScanTick = Date.now();   // no offset?
        // }
    }

    get monitored(): boolean {
        return this._monitored;
    }
    set monitored(value: boolean) {
        this._monitored = value;
    }

}

